import React, { Component } from 'react';
import { string, node, instanceOf, oneOf, oneOfType, object } from 'prop-types';
import ErrorDisplay from 'components/Error/ErrorDisplay/ErrorDisplay';
import ErrorPage from 'pages/ErrorPage';
import * as Sentry from '@sentry/react';
import getRuntimeEnv from "helpers/env";

/**
 * The ErrorBoundary component catches errors that are thrown further down in the tree.
 * Error boundaries cannot catch errors that occur in the component where componentDidCatch is defined
 */
export default class ErrorBoundary extends Component {
  state = { error: undefined, errorInfo: undefined };

  componentDidCatch(error, errorInfo) {
    // Error info is set to state in this method because getDerivedStateFromError doesn't receive it.
    this.setState({ error, errorInfo });
    this.logError(error, errorInfo);
  }

  componentDidUpdate() {
    if (this.props.error) {
      const { error, errorInfo } = this.props;
      this.logError(error, errorInfo);
    }
  }

  logError(error, errorInfo) {
    // Report the error to the console
    // eslint-disable-next-line no-console
    console.error('An error occurred somewhere in Opus', error);
    const env = getRuntimeEnv()

    // This would be where to report errors further down in the tree to an error logging service.
    if (
      env.REACT_APP_SENTRY_DSN &&
      env.NODE_ENV === 'production'
    ) {
      Sentry.withScope((scope) => {
        scope.setExtras(errorInfo);
        Sentry.captureException(error);
      });
    }
  }

  renderError(error, errorInfo) {
    const { type } = this.props;
    const ErrorDisplayComponent = type === 'page' ? ErrorPage : ErrorDisplay;

    return (
      <ErrorDisplayComponent
        className={this.props.className}
        error={error}
        errorInfo={errorInfo}
      />
    );
  }

  render() {
    const { children } = this.props;

    if (this.props.error instanceof Error) {
      const { error, errorInfo } = this.props;
      return this.renderError(error, errorInfo);
    }

    if (this.state.error instanceof Error) {
      const { error, errorInfo } = this.state;
      return this.renderError(error, errorInfo);
    }

    return children;
  }
}

ErrorBoundary.defaultProps = {
  type: 'component',
};

ErrorBoundary.propTypes = {
  /** The type of error to render. */
  type: oneOf(['page', 'component']),
  /** The child nodes of this component. */
  children: node.isRequired,
  /** A JavaScript-native Error object */
  error: instanceOf(Error),
  /** Additional error info, like a stack trace or a message */
  errorInfo: oneOfType([object, string]),
  /** Used by styled-components when composing components */
  className: string,
};
